/**
 * Turns `enumerateDevices()` output into something a viewfinder can offer the user.
 *
 * iOS 16.3+ exposes every physical rear lens as its own `videoinput` alongside virtual
 * "Dual"/"Triple" devices that auto-switch between them. Android generally exposes one
 * device per facing direction with labels that carry no lens information at all.
 *
 * Labels are the only signal available, they are localized, and they are empty until
 * camera permission has been granted. Everything here degrades to "no chips" rather than
 * guessing, because a lens button that does not change the lens is worse than no button.
 */

export const FRONT = 'user'
export const BACK = 'environment'

const FRONT_LABEL = /front|face[\s-]?time|selfie|user/i
const BACK_LABEL = /back|rear|environment|world/i

/** Virtual devices that fuse several lenses and switch on their own. */
const VIRTUAL_COMBO_LABEL = /dual|triple/i

/** Ordered: the ultra-wide test must run before the plain-wide test. */
const LENS_FACTOR_PATTERNS = [
  { pattern: /ultra[\s-]?wide/i, factor: 0.5 },
  { pattern: /telephoto/i, factor: 2 },
  { pattern: /\bwide\b/i, factor: 1 }
]

/**
 * Apple's label for the main rear lens, matched exactly. A loose "contains back" rule
 * would wrongly claim Android's "camera2 0, facing back" is a known 1x lens and offer
 * chips that do not switch anything.
 */
const APPLE_MAIN_LENS = /^\s*(back|rear)\s+camera\s*$/i

export function facingFromLabel(label) {
  const text = String(label || '')
  if (FRONT_LABEL.test(text)) return FRONT
  if (BACK_LABEL.test(text)) return BACK
  return null
}

export function isVirtualComboLens(label) {
  return VIRTUAL_COMBO_LABEL.test(String(label || ''))
}

/**
 * Best-effort zoom factor for a physical lens, or null when the label says nothing
 * useful. Virtual combo devices deliberately return null: they are not a fixed focal
 * length, so labelling one "1x" would be a lie.
 */
export function lensZoomFactor(label) {
  const text = String(label || '')
  if (!text) return null
  if (isVirtualComboLens(text)) return null

  for (const { pattern, factor } of LENS_FACTOR_PATTERNS) {
    if (pattern.test(text)) return factor
  }

  if (APPLE_MAIN_LENS.test(text)) return 1
  return null
}

function describeDevice(device) {
  const label = device?.label || ''
  return {
    deviceId: device?.deviceId || '',
    groupId: device?.groupId || '',
    label,
    facing: facingFromLabel(label),
    factor: lensZoomFactor(label),
    virtual: isVirtualComboLens(label)
  }
}

/**
 * @param {MediaDeviceInfo[]} devices
 * @returns {{ all: object[], front: object[], back: object[], unknown: object[] }}
 */
export function groupCameraDevices(devices) {
  const cameras = (Array.isArray(devices) ? devices : [])
    .filter((d) => d?.kind === 'videoinput' && d?.deviceId)
    .map(describeDevice)

  return {
    all: cameras,
    front: cameras.filter((c) => c.facing === FRONT),
    back: cameras.filter((c) => c.facing === BACK),
    unknown: cameras.filter((c) => c.facing === null)
  }
}

/**
 * Lens chips for one facing direction, ordered wide-to-long (0.5x, 1x, 2x).
 *
 * Returns an empty array unless at least two distinct physical focal lengths were
 * identified, since a single chip is just a label and offers the user no choice.
 */
export function buildLensChips(lenses) {
  const physical = (lenses || []).filter((lens) => !lens.virtual && lens.factor != null)

  const byFactor = new Map()
  for (const lens of physical) {
    if (!byFactor.has(lens.factor)) byFactor.set(lens.factor, lens)
  }

  if (byFactor.size < 2) return []

  return Array.from(byFactor.values())
    .sort((a, b) => a.factor - b.factor)
    .map((lens) => ({
      deviceId: lens.deviceId,
      factor: lens.factor,
      label: formatLensFactor(lens.factor)
    }))
}

export function formatLensFactor(factor) {
  if (!Number.isFinite(factor)) return ''
  return `${Math.round(factor * 10) / 10}x`
}

/**
 * The lens to open with for a facing direction: the plain 1x wide lens when we can
 * identify it, otherwise the virtual combo (which auto-switches and is the closest thing
 * to native behavior), otherwise whatever came first.
 */
export function defaultLensFor(lenses) {
  const list = lenses || []
  if (!list.length) return null
  return (
    list.find((lens) => !lens.virtual && lens.factor === 1) ||
    list.find((lens) => lens.virtual) ||
    list[0]
  )
}

/**
 * Whether a plain front/back flip control makes sense. True whenever both directions are
 * available, including when labels were too vague to build chips.
 */
export function canFlipFacing(grouped) {
  return Boolean(grouped?.front?.length && grouped?.back?.length)
}
